import type { Card } from '@/hooks/useCards'
import type { Profile } from '@/hooks/useProfile'
import { slugifyStoreName } from '@/lib/storeSlug'

export type StoreSort = 'recent' | 'newest' | 'oldest' | 'az' | 'za'

export interface StoreDirectoryItem {
  profile: Profile
  listingCount: number
  lastListedAt: string
}

export const STORE_SORT_OPTIONS: { value: StoreSort; label: string }[] = [
  { value: 'recent', label: 'Most recently active' },
  { value: 'newest', label: 'Newest stores' },
  { value: 'oldest', label: 'Oldest stores' },
  { value: 'az', label: 'A to Z' },
  { value: 'za', label: 'Z to A' },
]

function timestamp(value?: string) {
  const next = new Date(value || '').getTime()
  return Number.isNaN(next) ? 0 : next
}

export function storeDisplayName(profile: Profile) {
  return (profile.storeName || slugifyStoreName(profile.storeSlug) || 'Untitled store').trim()
}

export function buildStoreDirectory(profiles: Profile[], cards: Card[]): StoreDirectoryItem[] {
  const listingCount = new Map<string, number>()
  const lastListedAt = new Map<string, string>()

  for (const card of cards) {
    const userId = card.userId
    if (!userId) continue
    listingCount.set(userId, (listingCount.get(userId) || 0) + 1)
    const listedAt = card.updatedAt || card.createdAt
    const current = lastListedAt.get(userId)
    if (!current || timestamp(listedAt) > timestamp(current)) {
      lastListedAt.set(userId, listedAt)
    }
  }

  return profiles
    .filter(profile => Boolean(profile.storeName || profile.storeSlug))
    .map(profile => ({
      profile,
      listingCount: listingCount.get(profile.userId) || 0,
      lastListedAt: lastListedAt.get(profile.userId) || profile.updatedAt || profile.createdAt,
    }))
}

export function sortStoreDirectory(items: StoreDirectoryItem[], sort: StoreSort) {
  const next = [...items]
  next.sort((a, b) => {
    if (sort === 'az') return storeDisplayName(a.profile).localeCompare(storeDisplayName(b.profile))
    if (sort === 'za') return storeDisplayName(b.profile).localeCompare(storeDisplayName(a.profile))
    if (sort === 'oldest') return timestamp(a.profile.createdAt) - timestamp(b.profile.createdAt)
    if (sort === 'newest') return timestamp(b.profile.createdAt) - timestamp(a.profile.createdAt)
    const listed = timestamp(b.lastListedAt) - timestamp(a.lastListedAt)
    if (listed !== 0) return listed
    return timestamp(b.profile.updatedAt) - timestamp(a.profile.updatedAt)
  })
  return next
}
